import { useTranslation } from 'react-i18next'
import Dropzone from 'react-dropzone'
import { Box, Button, IconUpload, useToast } from '@machobear/design-system'
import { cleanFormData } from '@/ports/api'
import { videoRequest } from '@/models/listing/ports'

const fetchAWSParams = async (unitId: string): Promise<[string, any]> => {
  const response = await videoRequest(undefined, { unitId })
  if (response?.body) {
    const { formAttributes, formInputs } = response.body
    return [formAttributes.action, formInputs]
  }
  return ['', {}]
}

const postFile = ({ url, data }: { url: string; data: any }) =>
  new Promise((resolve, reject) => {
    const request = new XMLHttpRequest()
    request.open('POST', url)
    request.addEventListener('load', () => {
      const { status, response } = request
      if (status >= 200 && status < 300) {
        resolve({ status, response })
        return
      }
      reject({ status, response })
    })
    request.addEventListener('error', () => reject())
    request.addEventListener('abort', () => reject())
    request.send(data)
  })

interface Props {
  unitId: string
  label?: string
  isUploading: boolean
  setIsUploading: (value: boolean) => void
}

const VideoUploadBtn = ({ unitId, isUploading, setIsUploading }: Props) => {
  const { t } = useTranslation()
  const toast = useToast()
  const onSelect = async (files: File[]) => {
    if (isUploading) {
      return
    }

    setIsUploading(true)
    try {
      const [url, formInputs] = await fetchAWSParams(unitId)
      const data = cleanFormData({
        ...formInputs,
        success_action_status: 201,
        file: files[0]
      })
      await postFile({ url, data })
    } catch {
      toast({ message: 'File upload failed', tone: 'critical' })
      setIsUploading(false)
    }
  }

  return (
    <Dropzone
      onDrop={onSelect}
      maxFiles={1}
      accept={{
        'video/mpeg': ['.mpeg'],
        'video/mp4': ['.mp4'],
        'video/quicktime': ['.mov', '.qt'],
        'video/x-ms-wmv': ['.wmv'],
        'video/avi': ['.avi'],
        'video/msvideo': ['.avi'],
        'video/x-msvideo': ['.avi']
      }}
    >
      {({ getRootProps, getInputProps }) => (
        <Box {...getRootProps()}>
          <input {...getInputProps()} />
          <Button
            icon={<IconUpload />}
            variant="ghost"
            size="large"
            loading={isUploading}
            disabled={isUploading}
          >
            {isUploading ? t('Uploading') : t('Upload video')}
          </Button>
        </Box>
      )}
    </Dropzone>
  )
}

export default VideoUploadBtn
